import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Upload as UploadIcon, SmartToy as AIIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import PhysicianService from '../../services/PhysicianService';
import PatientService from '../../services/PatientService';
import { apiClient } from '../../services/api.js';

const MedicalRecordForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = Boolean(id);
  
  // Extract URL parameters
  const urlParams = new URLSearchParams(location.search);
  const prefilledPatientId = urlParams.get('patientId');
  const appointmentId = urlParams.get('appointmentId');
  const prefilledPhysicianId = urlParams.get('physicianId');
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiResponse, setAiResponse] = useState('');
  
  // Patient and physician lists for dropdown
  const [patients, setPatients] = useState([]);
  const [physicians, setPhysicians] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingPhysicians, setLoadingPhysicians] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    date: null,
    type: 'TCM Examination',
    physician: 'Dr. Johnson', // Changed from doctor to physician
    physicianId: '',
    diagnosis: '',
    symptoms: '',
    treatment: '',
    notes: '',
    // TCM-specific fields
    pulseDiagnosis: '',
    tongueExamination: '',
    constitutionAssessment: '',
    syndromePattern: '',
    herbalPrescription: '',
    acupuncturePoints: '',
    treatmentPrinciple: '',
    lifestyleAdvice: '',
    followUpPlan: '',
    attachments: []
  });
  
  useEffect(() => {
    // Fetch patients for dropdown
    const fetchPatients = async () => {
      try {
        setLoadingPatients(true);
        const response = await PatientService.getPatientsForDropdown();
        const patientsData = response.data || response;
        setPatients(patientsData);
        setLoadingPatients(false);
      } catch (error) {
        console.error('Error fetching patients:', error);
        setLoadingPatients(false);
      }
    };
    
    // Fetch physicians for dropdown
    const fetchPhysicians = async () => {
      try {
        setLoadingPhysicians(true);
        const response = await PhysicianService.getPhysiciansForDropdown();
        const physiciansData = Array.isArray(response) ? response : response.data || [];
        
        // Map the user data to physician format expected by the form
        const mappedPhysicians = physiciansData.map(physician => ({
          physicianId: String(physician.userId), // Use userId from the DTO
          firstName: physician.firstName,
          lastName: physician.lastName,
          email: physician.email
        }));
        
        setPhysicians(mappedPhysicians);
        setLoadingPhysicians(false);
      } catch (error) {
        console.error('Error fetching physicians:', error);
        setLoadingPhysicians(false);
      }
    };
    
    fetchPatients();
    fetchPhysicians();
    
    // Pre-fill form data from URL parameters (when coming from appointment)
    if (prefilledPatientId && !isEditMode) {
      setFormData(prev => ({
        ...prev,
        patientId: parseInt(prefilledPatientId),
        physicianId: prefilledPhysicianId || '',
        date: new Date() // Set to current date for new records
      }));
    }
    
    // If in edit mode, fetch medical record data
    if (isEditMode) {
      const fetchMedicalRecordData = async () => {
        try {
          setLoading(true);
          setError(null);
          
          // Import the MedicalRecordsService
          const MedicalRecordsService = (await import('../../services/MedicalRecordsService')).default;
          
          // Fetch medical record from the API
          const response = await MedicalRecordsService.getMedicalRecordById(id);
          const record = response.data;
          const recordId = record?.recordId ?? record?.RecordId;
          const patient = record?.patient ?? record?.Patient;
          const doctor = record?.doctor ?? record?.Doctor;
          
          // Transform the API data to match the form structure
          const recordDate = new Date(record?.visitDate ?? record?.VisitDate);
          
          setFormData({
            id: recordId,
            patientId: record?.patientId ?? record?.PatientId,
            patientName: patient ? `${patient.firstName ?? patient.FirstName ?? ''} ${patient.lastName ?? patient.LastName ?? ''}`.trim() : '',
            date: recordDate,
            type: record?.chiefComplaint ?? record?.ChiefComplaint ?? 'Examination',
            physician: doctor ? `${doctor.firstName ?? doctor.FirstName ?? ''} ${doctor.lastName ?? doctor.LastName ?? ''}`.trim() : '',
            physicianId: record?.doctorId ?? record?.DoctorId ?? '',
            diagnosis: record?.diagnosis ?? record?.Diagnosis ?? '',
            symptoms: record?.chiefComplaint ?? record?.ChiefComplaint ?? '',
            treatment: record?.treatment ?? record?.Treatment ?? '',
            notes: record?.notes ?? record?.Notes ?? '',
            // TCM-specific fields
            pulseDiagnosis: record?.pulseDiagnosis ?? record?.PulseDiagnosis ?? '',
            tongueExamination: record?.tongueExamination ?? record?.TongueExamination ?? '',
            constitutionAssessment: record?.constitutionAssessment ?? record?.ConstitutionAssessment ?? '',
            syndromePattern: record?.syndromePattern ?? record?.SyndromePattern ?? '',
            herbalPrescription: record?.herbalPrescription ?? record?.HerbalPrescription ?? '',
            acupuncturePoints: record?.acupuncturePoints ?? record?.AcupuncturePoints ?? '',
            treatmentPrinciple: record?.treatmentPrinciple ?? record?.TreatmentPrinciple ?? '',
            lifestyleAdvice: record?.lifestyleAdvice ?? record?.LifestyleAdvice ?? '',
            followUpPlan: record?.followUpPlan ?? record?.FollowUpPlan ?? '',
            attachments: [] // Will be loaded separately
          });
          
          // Load attachments for existing record
          if (recordId != null)
            loadAttachments(recordId);
          
          setLoading(false);
        } catch (error) {
          console.error('Error fetching medical record data:', error);
          setError('Failed to load medical record data. Please try again later.');
          setLoading(false);
        }
      };
      
      const loadAttachments = async (recordId) => {
        try {
          const response = await apiClient.get(`/attachment/medical-record/${recordId}`);
          
          const attachments = response.data.map(att => ({
            id: att.id,
            name: att.fileName,
            type: att.contentType.split('/')[0],
            size: `${(att.fileSize / (1024 * 1024)).toFixed(1)} MB`,
            uploadedAt: att.uploadedAt,
            uploadedBy: att.uploadedBy,
            isTemporary: false
          }));
          
          setFormData(prev => ({
            ...prev,
            attachments: attachments
          }));
          
        } catch (error) {
          console.error('Error loading attachments:', error);
          // Don't show error for attachments, just log it
        }
      };
      
      fetchMedicalRecordData();
    }
  }, [id, isEditMode]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      date: date
    });
  };
  
  const handlePatientChange = (e) => {
    const patientId = e.target.value;
    const selectedPatient = patients.find(patient => patient.patientId === patientId);
    
    setFormData({
      ...formData,
      patientId: patientId,
      patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : ''
    });
  };
  
  const handlePhysicianChange = (e) => {
    const physicianId = e.target.value;
    const selectedPhysician = physicians.find(physician => physician.physicianId === physicianId);
    
    setFormData({
      ...formData,
      physicianId: physicianId,
      physician: selectedPhysician ? `${selectedPhysician.firstName} ${selectedPhysician.lastName}` : ''
    });
  };

  const handleAIAssistant = async () => {
    setAiOpen(true);
    setAiLoading(true);
    setAiError(null);
    setAiResponse('');
    try {
      const patientId = Number(formData.patientId);
      if (!Number.isFinite(patientId) || patientId <= 0) {
        setAiError('Please select a patient first.');
        return;
      }

      const payload = {
        patientId,
        pulseDiagnosis: formData.pulseDiagnosis,
        tongueExamination: formData.tongueExamination,
        constitutionAssessment: formData.constitutionAssessment,
        syndromePattern: formData.syndromePattern,
        herbalPrescription: formData.herbalPrescription,
        acupuncturePoints: formData.acupuncturePoints,
        treatmentPrinciple: formData.treatmentPrinciple,
        lifestyleAdvice: formData.lifestyleAdvice,
        notes: formData.notes,
      };
      const res = await apiClient.post('/ai/assistant', payload);
      const text = res.data?.text || '';
      setAiResponse(text);
    } catch (e) {
      setAiError(e.response?.data?.message || e.message || 'Failed to run AI assistant');
    } finally {
      setAiLoading(false);
    }
  };
  
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (!formData.id && !isEditMode) {
      // For new records, store files temporarily until record is saved
      const newAttachments = files.map((file, index) => ({
        id: Date.now() + index,
        name: file.name,
        type: file.type.split('/')[0],
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        uploadedAt: new Date().toISOString(),
        file: file, // Store the actual file for later upload
        isTemporary: true
      }));
      
      setFormData({
        ...formData,
        attachments: [...formData.attachments, ...newAttachments]
      });
      return;
    }
    
    // For existing records, upload files immediately
    const recordId = formData.id || id;
    if (!recordId) return;
    
    try {
      const uploadPromises = files.map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await apiClient.post(
          `/attachment/upload/${recordId}`,
          formDataUpload,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        return {
          id: response.data.id,
          name: response.data.fileName,
          type: response.data.contentType.split('/')[0],
          size: `${(response.data.fileSize / (1024 * 1024)).toFixed(1)} MB`,
          uploadedAt: response.data.uploadedAt,
          isTemporary: false
        };
      });
      
      const uploadedAttachments = await Promise.all(uploadPromises);
      
      setFormData({
        ...formData,
        attachments: [...formData.attachments, ...uploadedAttachments]
      });
      
    } catch (error) {
      console.error('Error uploading files:', error);
      setError('Failed to upload files. Please try again.');
    }
  };
  
  const handleRemoveAttachment = async (attachmentId) => {
    const attachment = formData.attachments.find(att => att.id === attachmentId);
    
    if (!attachment) return;
    
    // If it's a temporary attachment (not yet uploaded), just remove from state
    if (attachment.isTemporary) {
      setFormData({
        ...formData,
        attachments: formData.attachments.filter(att => att.id !== attachmentId)
      });
      return;
    }
    
    // For uploaded attachments, delete from server
    try {
      await apiClient.delete(`/attachment/${attachmentId}`);
      
      setFormData({
        ...formData,
        attachments: formData.attachments.filter(att => att.id !== attachmentId)
      });
      
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setError('Failed to delete attachment. Please try again.');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    
    // Validate form data
    if (!formData.patientId || !formData.date || !formData.type || !formData.diagnosis || !formData.syndromePattern) {
      setError('Please fill in all required fields.');
      setSaving(false);
      return;
    }
    
    try {
      // Import the MedicalRecordsService
      const MedicalRecordsService = (await import('../../services/MedicalRecordsService')).default;
      
      // Prepare the medical record data for the API
      const medicalRecordData = {
        recordId: isEditMode ? formData.id : 0,
        patientId: formData.patientId,
        visitDate: formData.date.toISOString(),
        chiefComplaint: formData.type, // Using type as chief complaint
        diagnosis: formData.diagnosis,
        treatment: formData.treatment,
        notes: formData.notes,
        doctorId: formData.physicianId || 0,
        // TCM-specific fields
        pulseDiagnosis: formData.pulseDiagnosis,
        tongueExamination: formData.tongueExamination,
        constitutionAssessment: formData.constitutionAssessment,
        syndromePattern: formData.syndromePattern,
        herbalPrescription: formData.herbalPrescription,
        acupuncturePoints: formData.acupuncturePoints,
        treatmentPrinciple: formData.treatmentPrinciple,
        lifestyleAdvice: formData.lifestyleAdvice,
        followUpPlan: formData.followUpPlan
      };
      
      // Call the API to create or update the medical record
      let recordId;
      if (isEditMode) {
        await MedicalRecordsService.updateMedicalRecord(formData.id, medicalRecordData);
        recordId = formData.id;
      } else {
        const response = await MedicalRecordsService.createMedicalRecord(medicalRecordData);
        recordId = response.data?.id || response.id;
      }
      
      // Upload temporary attachments for new records
      if (!isEditMode && formData.attachments.some(att => att.isTemporary)) {
        const temporaryAttachments = formData.attachments.filter(att => att.isTemporary);
        
        try {
          const uploadPromises = temporaryAttachments.map(async (attachment) => {
            const formDataUpload = new FormData();
            formDataUpload.append('file', attachment.file);
            
            return await apiClient.post(
              `/attachment/upload/${recordId}`,
              formDataUpload,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
          });
          
          await Promise.all(uploadPromises);
        } catch (attachmentError) {
          console.error('Error uploading attachments:', attachmentError);
          // Don't fail the entire operation if attachments fail
          setError('Medical record saved, but some attachments failed to upload.');
        }
      }
      
      setSuccess(true);
      setSaving(false);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/medical-records');
      }, 1500);
    } catch (error) {
      console.error('Error saving medical record:', error);
      setError('Failed to save medical record. Please try again.');
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  const recordTypes = [
    'Initial Consultation', // 初诊
    'Follow-up Consultation', // 复诊
    'TCM Examination', // 中医体检
    'Acupuncture Treatment', // 针灸治疗
    'Herbal Medicine Consultation', // 中药咨询
    'Cupping Therapy', // 拔罐治疗
    'Tuina Massage', // 推拿按摩
    'Moxibustion Treatment', // 艾灸治疗
    'Pulse Diagnosis', // 脉诊
    'Tongue Examination', // 舌诊
    'Constitution Assessment', // 体质辨识
    'Other'
  ];
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 1 }}
            component={RouterLink}
            to="/medical-records"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {isEditMode ? 'Edit Medical Record' : 'New Medical Record'}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<AIIcon />}
            onClick={handleAIAssistant}
            disabled={aiLoading || saving || !formData.patientId}
          >
            {aiLoading ? 'Running...' : 'AI assistant'}
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Medical record {isEditMode ? 'updated' : 'created'} successfully!
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="patient-label">Patient</InputLabel>
                  <Select
                    labelId="patient-label"
                    id="patientId"
                    name="patientId"
                    value={formData.patientId}
                    onChange={handlePatientChange}
                    label="Patient"
                    disabled={saving || loadingPatients}
                  >
                    {patients.map((patient) => (
                      <MenuItem key={patient.patientId} value={patient.patientId}>
                        {`${patient.firstName} ${patient.lastName}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date *"
                    value={formData.date}
                    onChange={handleDateChange}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                    disabled={saving}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        required: true
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="type-label">Record Type</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  label="Record Type"
                  disabled={saving}
                >
                  {recordTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
              
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel id="physician-label">Practitioner</InputLabel>
                <Select
                  labelId="physician-label"
                  id="physicianId"
                  name="physicianId"
                  value={formData.physicianId}
                  onChange={handlePhysicianChange}
                  label="Practitioner"
                  disabled={saving || loadingPhysicians}
                >
                  {physicians.map((physician) => (
                    <MenuItem key={physician.physicianId} value={physician.physicianId}>
                      {`${physician.firstName} ${physician.lastName}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Traditional Chinese Medicine Assessment
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pulse Diagnosis (脉诊)"
                name="pulseDiagnosis"
                value={formData.pulseDiagnosis}
                onChange={handleInputChange}
                multiline
                rows={2}
                disabled={saving}
                placeholder="e.g., Floating, rapid pulse; Weak, thready pulse"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tongue Examination (舌诊)"
                name="tongueExamination"
                value={formData.tongueExamination}
                onChange={handleInputChange}
                multiline
                rows={2}
                disabled={saving}
                placeholder="e.g., Red tongue with yellow coating; Pale tongue with white coating"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Constitution Assessment (体质辨识)"
                name="constitutionAssessment"
                value={formData.constitutionAssessment}
                onChange={handleInputChange}
                multiline
                rows={2}
                disabled={saving}
                placeholder="e.g., Yang deficiency constitution; Phlegm-dampness constitution"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Syndrome Pattern (证候)"
                name="syndromePattern"
                value={formData.syndromePattern}
                onChange={handleInputChange}
                multiline
                rows={2}
                disabled={saving}
                placeholder="e.g., Liver Qi stagnation; Kidney Yang deficiency"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Herbal Prescription (中药处方)"
                name="herbalPrescription"
                value={formData.herbalPrescription}
                onChange={handleInputChange}
                multiline
                rows={3}
                disabled={saving}
                placeholder="e.g., Gan Mai Da Zao Tang, Dosage: 15g twice daily"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Acupuncture Points (针灸穴位)"
                name="acupuncturePoints"
                value={formData.acupuncturePoints}
                onChange={handleInputChange}
                multiline
                rows={2}
                disabled={saving}
                placeholder="e.g., Baihui (GV20), Yintang (EX-HN3), Shenmen (HE7)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Treatment Principle (治法)"
                name="treatmentPrinciple"
                value={formData.treatmentPrinciple}
                onChange={handleInputChange}
                multiline
                rows={2}
                disabled={saving}
                placeholder="e.g., Tonify Kidney Yang; Clear Heat and Detoxify"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Lifestyle Advice (生活调理建议)"
                name="lifestyleAdvice"
                value={formData.lifestyleAdvice}
                onChange={handleInputChange}
                multiline
                rows={2}
                disabled={saving}
                placeholder="e.g., Avoid cold foods, maintain regular sleep schedule"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Follow-up Plan (复诊计划)"
                name="followUpPlan"
                value={formData.followUpPlan}
                onChange={handleInputChange}
                multiline
                rows={2}
                disabled={saving}
                placeholder="e.g., Return in 1 week for reassessment"
              />
            </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Diagnosis"
                  name="diagnosis"
                  value={formData.diagnosis}
                  onChange={handleInputChange}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Symptoms"
                  name="symptoms"
                  value={formData.symptoms}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Treatment"
                  name="treatment"
                  value={formData.treatment}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  disabled={saving}
                />
              </Grid>
              
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Attachments
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <input
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    style={{ display: 'none' }}
                    id="file-upload"
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    disabled={saving}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      disabled={saving}
                    >
                      Upload Files
                    </Button>
                  </label>
                </Box>
                
                {formData.attachments.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {formData.attachments.map((attachment) => (
                      <Box 
                        key={attachment.id}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1,
                          '&:not(:last-child)': { borderBottom: '1px solid #eee' }
                        }}
                      >
                        <Typography variant="body2">
                          {attachment.name} ({attachment.size})
                        </Typography>
                        <Button 
                          size="small" 
                          color="error"
                          onClick={() => handleRemoveAttachment(attachment.id)}
                          disabled={saving}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    sx={{ mr: 1 }}
                    component={RouterLink}
                    to="/medical-records"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        Saving...
                      </>
                    ) : (
                      isEditMode ? 'Update Record' : 'Create Record'
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
      <Dialog open={aiOpen} onClose={() => setAiOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>AI Assistant</DialogTitle>
        <DialogContent dividers>
          {aiLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {aiError && (
            <Alert severity="error" sx={{ mb: 2 }}>{aiError}</Alert>
          )}
          {aiResponse && (
            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {aiResponse}
            </Box>
          )}
          {!aiLoading && !aiError && !aiResponse && (
            <Typography variant="body2">No response.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalRecordForm;
